import React from "react";
import { Alert, Button, Spin } from "antd";
import { FileTextOutlined, LockOutlined, MailOutlined, ReloadOutlined } from "@ant-design/icons";
import marinBytesLogo from "../../images/logo.jpeg";
import "./subscription.css";

const content = {
  no_subscription: ["Subscription required", "Activate your company workspace", "Your company does not currently have an active subscription. Choose an available free trial or contact our team to arrange access."],
  trial_expired: ["Free trial ended", "Your free trial has ended", "Thank you for evaluating MarinBytes. Contact our team to arrange an invoice-based subscription and restore access to your company workspace."],
  subscription_expired: ["Subscription ended", "Your subscription has ended", "Contact our team to renew your invoice-based subscription and restore access to your company workspace."],
  subscription_scheduled: ["Subscription scheduled", "Your subscription has not started yet", "Your subscription is scheduled for a future date. Contact our team if you need to review the activation date."],
  vessel_not_subscribed: ["Vessel access required", "This vessel needs subscription access", "Contact your company account owner to include this vessel in the company subscription."],
  account_inactive: ["Account unavailable", "Your company account needs attention", "Contact our team to review your company account and arrange access."],
};

function utcDate(value) {
  if (!value || !Number.isFinite(Date.parse(value))) return null;
  return new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "UTC" }).format(new Date(value)) + " UTC";
}

export default function SubscriptionAccessScreen({ access, companyName, onLogout }) {
  const { subscription, loading, error, starting, trialError, refresh, startTrial } = access;
  const [label, title, description] = content[subscription?.state] || content.no_subscription;
  const trial = subscription?.trial;
  const months = trial?.duration_months;
  const emails = subscription?.contact_emails || [];
  const endedAt = subscription?.state === "trial_expired" ? utcDate(trial?.ends_at) : null;

  return (
    <div className="subscription-page">
      <header className="subscription-header">
        <img className="subscription-brand" src={marinBytesLogo} alt="MarinBytes — Data, Ships, People and Sustainability" width="1080" height="713" />
        <Button onClick={onLogout}>Sign out</Button>
      </header>
      <main className="subscription-main" aria-labelledby="subscription-title">
        {loading ? (
          <div className="subscription-status" role="status"><Spin size="large" /><h1 id="subscription-title">Verifying company access</h1><p>Please wait while we check your subscription.</p></div>
        ) : error ? (
          <div className="subscription-status"><h1 id="subscription-title">Access status unavailable</h1><Alert type="warning" showIcon message={error} /><Button onClick={refresh} icon={<ReloadOutlined aria-hidden="true" />}>Try again</Button></div>
        ) : (
          <>
            <div className="subscription-intro">
              <span className="subscription-eyebrow">COMPANY WORKSPACE</span>
              {companyName && <p className="subscription-company">{companyName}</p>}
              <span className="subscription-badge"><LockOutlined aria-hidden="true" /> {label}</span>
              <h1 id="subscription-title">{title}</h1>
              <p className="subscription-description">{subscription?.state === "no_subscription" && !trial?.eligible
                ? "Your company does not currently have an active subscription. Contact our team to arrange an invoice-based subscription."
                : description}</p>
              {endedAt && <p className="subscription-date">Trial ended: {endedAt}</p>}
              {subscription?.state === "subscription_expired" && subscription?.paid?.ends_on && <p className="subscription-date">Subscription end date: {subscription.paid.ends_on} (UTC)</p>}
              {subscription?.state === "subscription_scheduled" && <p className="subscription-date">Scheduled start: {subscription.paid?.starts_on} (UTC)</p>}
            </div>
            <div className={`subscription-options ${trial?.eligible ? "subscription-options--two" : ""}`}>
              {trial?.eligible && (
                <section className="subscription-card subscription-card--trial" aria-labelledby="trial-title">
                  {/* Show the ship emblem from the same MarinBytes brand artwork. */}
                  <svg className="subscription-brand-mark" viewBox="95 75 910 375" preserveAspectRatio="xMidYMax meet" aria-hidden="true" focusable="false">
                    <image href={marinBytesLogo} width="1080" height="713" />
                  </svg>
                  <span className="subscription-card-label">COMPLIMENTARY ACCESS</span>
                  <h2 id="trial-title">Start your free trial</h2>
                  <p>Evaluate MarinBytes with your team for {months} {months === 1 ? "calendar month" : "calendar months"}.</p>
                  <ul><li>One free trial per company</li><li>Starts immediately upon activation</li><li>No automatic paid subscription</li></ul>
                  <Button type="primary" size="large" loading={starting} disabled={starting} onClick={startTrial}>Start {months}-month free trial</Button>
                  {trialError && <Alert className="subscription-trial-error" type="error" showIcon message={trialError} />}
                </section>
              )}
              <section className="subscription-card" aria-labelledby="invoice-title">
                <div className="subscription-icon"><FileTextOutlined aria-hidden="true" /></div>
                <span className="subscription-card-label">INVOICE-BASED SUBSCRIPTION</span>
                <h2 id="invoice-title">Speak with our team</h2>
                <p>For invoice-based subscription activation or renewal, contact us at:</p>
                <div className="subscription-contacts">
                  {emails.map((email) => (
                    <div key={email} className="subscription-contact">
                      <MailOutlined aria-hidden="true" />
                      <span className="subscription-contact__email">{email}</span>
                    </div>
                  ))}
                  {!emails.length && <p>Please contact your MarinBytes account representative for subscription assistance.</p>}
                </div>
                <p className="subscription-note">Include your company name and vessel requirements in your enquiry.</p>
              </section>
            </div>
            {!subscription?.can_manage && <p className="subscription-owner-note">Subscription access is managed by your company account owner. Please contact them for assistance.</p>}
            {trial?.used && <p className="subscription-owner-note">Your company’s one-time free trial has already been used.</p>}
            {trialError && !trial?.eligible && <Alert type="info" showIcon message={trialError} />}
            <div className="subscription-refresh"><span>Already arranged your subscription?</span><Button type="link" icon={<ReloadOutlined aria-hidden="true" />} onClick={refresh}>Refresh access status</Button></div>
          </>
        )}
      </main>
      <footer className="subscription-footer">MarinBytes · Company subscription access</footer>
    </div>
  );
}
